/**
 * Workspace "AI access" limits (#976) — SUSPENDED until BPF sandbox enforcement is
 * fully implemented (R1–R6 on every host).
 *
 * A workspace owner can cap what the agent may touch per file / folder (`ai: 'r'` or
 * `ai: 'hidden'`, stored in the workspace's own `.config/workspace.yaml`). In `mounts`
 * mode that enforcement has gaps that cannot be closed while the mode is frozen —
 * a `.chain` / `.shared` inside an `r` folder writes through (#917), a pointer
 * ELSEWHERE whose target is under a `hidden` folder exposes it, an `r` entry under a
 * `hidden` folder re-opens it read-only, and a stale pin costs every turn (#283).
 *
 * So the feature is SUSPENDED, not removed:
 *   - enforcement is off — every enforcement site reads the entries through
 *     `effectiveAIAccess`, which yields none while suspended;
 *   - every stored entry is PRESERVED — nothing is dropped, downgraded or re-pointed (only
 *     its path keeps following its folder, and a relink re-resolves the ids it moved), so
 *     turning the feature back on restores the same settings;
 *   - no new entry can be set through the app (the route refuses, the menu is remove-only);
 *     removing one stays allowed to anyone who can write the workspace config, as before.
 *
 * Re-enabling is a reviewed code change that follows the checklist on #976 — never a
 * per-host setting, which is why this is a constant and not an env var.
 *
 * Code-side additions to that checklist (surfaces built while suspended):
 *   - `move_file` (features/agent/tools/move-file.ts + apps/worker/src/processors/agent-drive-move.ts) moves
 *     through the DRIVE, not the sandbox: its only AI-limit gate is `checkWritable` on the source and the
 *     destination path. On re-enable, confirm an `ai: 'r'` / `'hidden'` entry refuses a move out of / into its
 *     folder, and that a drive move by the agent doesn't carry an item OUT of a limited folder (or a limited item
 *     elsewhere) in a way the limit — which follows its folder by id — no longer covers.
 */
export const AI_ACCESS_SUSPENDED = true

/** Error code the API answers when a new AI access limit is refused while suspended. */
export const AI_ACCESS_SUSPENDED_CODE = 'ai_access_suspended'

/**
 * The AI access entries that are ENFORCED: none while suspended, else all of them.
 * Every enforcement site reads entries through this; sites that only display or store
 * them read the raw list. (`suspended` is a parameter so tests can run both sides.)
 */
export function effectiveAIAccess<T>(entries: readonly T[] | null | undefined, suspended: boolean = AI_ACCESS_SUSPENDED): T[] {
  if (suspended || !entries) return []
  return [...entries]
}

/**
 * The ENFORCED form of one stored limit — `undefined` while suspended. For UI that ACTS
 * on a limit (withholding a file from the chat context, dimming an AI-hidden subtree);
 * UI that only shows the stored setting (the "paused" badge) reads the raw value.
 */
export function enforcedAIOverride<T extends string>(override: T | undefined, suspended: boolean = AI_ACCESS_SUSPENDED): T | undefined {
  return suspended ? undefined : override
}
